import React, { useState, createContext, useContext, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import { IconButton, Popover, Box, useTheme, useMediaQuery } from '@mui/material';
import { CustomButton } from '@/components/global/buttons/CustomButton';
import DotsIcon from '@/assets/icons/dots-icon.svg?react';
import { Palette } from '@/themes/themeOverrides/Palette';

const PopoverContext = createContext();

export const usePopoverContext = () => useContext(PopoverContext) || { closePopover: () => {} };

const CustomMorePopover = ({ children, record, buttonLabel, className = '', color = 'primary' }) => {
    const [anchorEl, setAnchorEl] = useState(null);
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));

    const handleOpen = event => {
        event.stopPropagation();
        setAnchorEl(event.currentTarget);
    };

    const handleClose = useCallback(event => {
        event?.stopPropagation?.();
        setAnchorEl(null);
    }, []);

    const stopRowClick = event => {
        event.stopPropagation();
    };

    const popoverContextValue = useMemo(() => ({ closePopover: handleClose }), [handleClose]);

    if (!record) return null;

    const open = Boolean(anchorEl);
    const id = open ? 'more-actions-popover' : undefined;

    return (
        <PopoverContext.Provider value={popoverContextValue}>
            {buttonLabel ? (
                <CustomButton
                    color={color}
                    label={buttonLabel}
                    variant='contained'
                    endIcon={<DotsIcon />}
                    onClick={handleOpen}
                    noLabelOnMobile={true}
                    className={open ? 'active' : ''}
                    sx={{
                        height: '44px',
                        marginLeft: 'auto',
                    }}
                />
            ) : (
                <IconButton onClick={handleOpen} className={open ? 'active' : ''} sx={{ borderRadius: '10px' }}>
                    <DotsIcon />
                </IconButton>
            )}
            <Popover
                id={id}
                open={open}
                anchorEl={anchorEl}
                onClose={handleClose}
                className={`more-area ${className}`}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'right',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                }}
                disableScrollLock={!isMobile}
                disableEnforceFocus
                ModalProps={{
                    onClick: stopRowClick,
                    onMouseDown: stopRowClick,
                }}
                sx={{
                    '& .MuiPaper-rounded.MuiPopover-paper': {
                        marginTop: 0,
                    },
                    '& .MuiButtonBase-root.MuiButton-root': {
                        padding: '12px',
                        lineHeight: '20px',
                        borderRadius: '8px',
                        '&:hover': {
                            backgroundColor: `${Palette.yellow.yellow50} !important`,
                        },
                    },
                }}
            >
                <Box
                    className='more-content'
                    sx={{
                        '& > *': {
                            width: '100%',
                            justifyContent: 'flex-start !important',
                        },
                    }}
                    onClick={event => {
                        event.stopPropagation();
                        const trigger = event.target.closest('button');
                        if (!trigger) return;
                        if (trigger.closest('[data-keep-popover-open="true"]')) return;
                        handleClose();
                    }}
                >
                    {children}
                </Box>
            </Popover>
        </PopoverContext.Provider>
    );
};

CustomMorePopover.propTypes = {
    children: PropTypes.node,
    record: PropTypes.object,
    buttonLabel: PropTypes.string,
    className: PropTypes.string,
    color: PropTypes.string,
};

export default CustomMorePopover;
